import React from "react";

import { lighten, makeStyles } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";

import {
  Toolbar,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  AppBar,
  Button,
  IconButton,
  Tooltip,
} from "@material-ui/core";

import { KeyboardDatePicker } from "@material-ui/pickers";

import SearchIcon from "@material-ui/icons/Search";
import ClearAllIcon from "@material-ui/icons/ClearAll";

const useToolbarStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },

  toolbarMobile: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
    },
  },

  title: {
    display: "none",

    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },

  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: lighten(theme.palette.common.white, 0.15),
    marginRight: theme.spacing(1),
    marginLeft: 0,
    width: "100%",

    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(1),
      width: "auto",
    },
  },

  secondSession: {
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
  },

  clientTotalDiv: {
    background: "#fafafa",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    flexDirection: "row",
    lineHeight: "0.78em",
    fontSize: "12px",
    alignItems: "flex-end",
    margin: "10px",

    "& span": {
      marginRight: "1em",
      backgroundColor: lighten(theme.palette.info.light, 0.7),
      padding: "10px 6px",
      borderRadius: 4,
    },

    "& .nagativeAmount": {
      marginRight: "1em",
      backgroundColor: lighten(theme.palette.secondary.light, 0.7),
      padding: "10px 6px",
      borderRadius: 4,
    },

    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
      marginBottom: "6px",
      lineHeight: "1.4em",
      fontSize: 10,
    },
  },

  sectionDesktop: {
    display: "none",

    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "center",
    },
  },

  sectionMobile: {
    display: "flex",
    flexDirection: "row",
    paddingTop: 2,

    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
}));

const EnhancedTableToolbar = ({
  title,
  handleChange,
  handleChangeAutoComplete,
  values,
  isClients,
  handleInputChange,
  handleFromDateChange,
  handleToDateChange,
  searchHandler,
  clearSearchHandler,
  totalAcAmount,
  totalIpAmount,
  totalService,
  totalvat,
  lilAmount,
}) => {
  const classes = useToolbarStyles();

  return (
    <div className={classes.grow}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar className={classes.toolbarMobile}>
          <Typography className={classes.title} variant="h6" noWrap>
            {title}
          </Typography>

          {/* Company */}
          <div className={classes.search}>
            <Autocomplete
              id="account-company"
              options={isClients || []}
              getOptionLabel={(option) => option?.companyName || ""}
              onChange={handleChangeAutoComplete("company")}
              value={values.company || null}
              style={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company"
                  size="small"
                  variant="outlined"
                  onChange={handleInputChange}
                />
              )}
            />
          </div>

          <div className={classes.grow} />

          <div className={classes.sectionDesktop}>
            {/* Private */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.businessType === "Private"}
                  onChange={handleChange}
                  name="businessType"
                  color="primary"
                />
              }
              label="Private"
            />

            {/* From */}
            <KeyboardDatePicker
              autoOk
              size="small"
              format="dd/MM/yyyy"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              variant="inline"
              inputVariant="outlined"
              label="From"
              onChange={handleFromDateChange}
              value={values.fromDate}
            />

            {/* To */}
            <KeyboardDatePicker
              autoOk
              size="small"
              format="dd/MM/yyyy"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              variant="inline"
              inputVariant="outlined"
              label="To"
              onChange={handleToDateChange}
              value={values.toDate}
            />

            {/* Search */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              &nbsp;
              <Button
                size="small"
                endIcon={<SearchIcon />}
                onClick={searchHandler}
              >
                Search
              </Button>
              {values.isSearch && (
                <Tooltip title="Clear search">
                  <IconButton
                    color="primary"
                    aria-label="clear search inputs"
                    onClick={clearSearchHandler}
                  >
                    <ClearAllIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Mobile */}
          <div className={classes.sectionMobile}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.businessType === "Private"}
                  onChange={handleChange}
                  name="businessType"
                  color="primary"
                />
              }
              label="Private"
            />
          </div>
        </Toolbar>
      </AppBar>

      {/* Account totals */}
      <div className={classes.secondSession}>
        <div />

        <div className={classes.clientTotalDiv}>
          <span>
            <strong>Total Billing</strong> {totalAcAmount?.toFixed(2)}
          </span>

          <span>
            <strong>IP Amount</strong> {totalIpAmount?.toFixed(2)}
          </span>

          <span className="nagativeAmount">
            <strong>LIL Amount</strong> {lilAmount?.toFixed(2)}
          </span>

          <span>
            <strong>Total Service Charges</strong> {totalService?.toFixed(2)}
          </span>

          <span>
            <strong>Total VAT</strong> {totalvat?.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(EnhancedTableToolbar);
